import React, { useState, useEffect } from 'react';
import { 
  CreditCard, 
  Plus, 
  Trash2,
  DollarSign,
  Building2,
  CheckCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { useSettings } from '../context/SettingsContext';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const paymentMethods = [
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'cash', label: 'Cash' },
  { value: 'check', label: 'Check' },
  { value: 'card', label: 'Card' },
];

const SupplierPayments = () => {
  const { currencySymbol } = useSettings();
  const [payments, setPayments] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    fetchPayments();
    fetchSuppliers();
    fetchBills();
  }, []);

  const fetchPayments = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/procurement/payments`);
      setPayments(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/procurement/suppliers`);
      setSuppliers(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchBills = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/procurement/bills`);
      setBills(res.data.filter(b => b.status !== 'paid'));
    } catch (err) {
      console.error(err);
    }
  };

  const handleSave = async () => {
    try {
      await axios.post(`${API_URL}/api/procurement/payments`, formData);
      toast.success('Payment recorded');
      setShowDialog(false);
      setFormData({});
      fetchPayments();
      fetchBills();
    } catch (err) {
      toast.error('Failed to record payment');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this payment?')) {
      try {
        await axios.delete(`${API_URL}/api/procurement/payments/${id}`);
        toast.success('Payment deleted');
        fetchPayments();
      } catch (err) {
        toast.error('Failed to delete');
      }
    }
  };

  const openNew = () => {
    setFormData({ payment_method: 'bank_transfer', amount: 0 });
    setShowDialog(true);
  };

  const stats = {
    total: payments.length,
    totalPaid: payments.reduce((sum, p) => sum + p.amount, 0),
    thisMonth: payments.filter(p => {
      const d = new Date(p.date);
      const now = new Date();
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    }).reduce((sum, p) => sum + p.amount, 0)
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Supplier Payments</h1>
          <p className="text-muted-foreground">Record payments made to suppliers.</p>
        </div>
        <Button onClick={openNew} className="gap-2"><Plus className="h-4 w-4" /> Record Payment</Button>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <Card><CardContent className="pt-6"><div className="text-2xl font-bold">{stats.total}</div><div className="text-sm text-muted-foreground">Total Payments</div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="text-2xl font-bold text-emerald-600">{currencySymbol} {stats.totalPaid.toLocaleString()}</div><div className="text-sm text-muted-foreground">Total Paid</div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="text-2xl font-bold text-blue-600">{currencySymbol} {stats.thisMonth.toLocaleString()}</div><div className="text-sm text-muted-foreground">This Month</div></CardContent></Card>
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Payment #</TableHead>
              <TableHead>Supplier</TableHead>
              <TableHead>Bill #</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Method</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Reference</TableHead>
              <TableHead className="w-[50px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {payments.map((payment) => (
              <TableRow key={payment.id}>
                <TableCell className="font-mono font-medium">{payment.payment_number}</TableCell>
                <TableCell>{payment.supplier_name}</TableCell>
                <TableCell className="font-mono text-muted-foreground">{payment.bill_number || '-'}</TableCell>
                <TableCell className="text-muted-foreground">{format(new Date(payment.date), 'MMM d, yyyy')}</TableCell>
                <TableCell>
                  <Badge variant="outline">
                    {paymentMethods.find(m => m.value === payment.payment_method)?.label || payment.payment_method}
                  </Badge>
                </TableCell>
                <TableCell className="text-right font-mono font-bold text-emerald-600">{currencySymbol} {payment.amount?.toLocaleString()}</TableCell>
                <TableCell className="text-muted-foreground">{payment.reference || '-'}</TableCell>
                <TableCell>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDelete(payment.id)}>
                    <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {payments.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="h-32 text-center">
                  <CreditCard className="h-8 w-8 mx-auto text-muted-foreground/30 mb-2" />
                  <p className="text-muted-foreground">No payments recorded</p>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Create Payment Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Record Payment</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Supplier *</Label>
              <Select value={formData.supplier_id || ''} onValueChange={v => setFormData({...formData, supplier_id: v, bill_id: ''})}>
                <SelectTrigger><SelectValue placeholder="Select supplier" /></SelectTrigger>
                <SelectContent>{suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Link to Bill (Optional)</Label>
              <Select value={formData.bill_id || 'none'} onValueChange={v => {
                const bill = bills.find(b => b.id === v);
                setFormData({...formData, bill_id: v === 'none' ? '' : v, amount: bill ? bill.total - bill.paid_amount : formData.amount});
              }}>
                <SelectTrigger><SelectValue placeholder="Select bill" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {bills.filter(b => b.supplier_id === formData.supplier_id).map(b => (
                    <SelectItem key={b.id} value={b.id}>
                      {b.bill_number} - Due: {currencySymbol} {(b.total - b.paid_amount).toLocaleString()}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Amount *</Label>
                <Input type="number" step="0.01" value={formData.amount || ''} onChange={e => setFormData({...formData, amount: parseFloat(e.target.value) || 0})} />
              </div>
              <div className="space-y-2">
                <Label>Date</Label>
                <Input type="date" value={formData.date || ''} onChange={e => setFormData({...formData, date: e.target.value})} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Payment Method</Label>
              <Select value={formData.payment_method || 'bank_transfer'} onValueChange={v => setFormData({...formData, payment_method: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {paymentMethods.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Reference #</Label>
              <Input value={formData.reference || ''} onChange={e => setFormData({...formData, reference: e.target.value})} placeholder="Check #, Transaction ID, etc." />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={formData.notes || ''} onChange={e => setFormData({...formData, notes: e.target.value})} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave}>
              <CheckCircle className="h-4 w-4 mr-2" /> Record Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SupplierPayments;
